import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row, Table, Dropdown, Pagination } from "react-bootstrap";
import { Common, EmployeeIcons, SettingsIcons } from "../../assets/images";
import { useNavigate } from "react-router-dom";
// Redux Imports
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setShowActiveExportDropdown,
  setActivePageSize,
  setActivePageNo,
  setFilterActivePageNo,
  setFilterActivePageSize,
} from "../../store/slices/employeeSlice";
import { setEmployeeChange } from "../../store/slices/commonSlice";
import { LoaderBlue } from "../Lotties";
// API Imports
import { useDeleteEmployeeMutation } from "../../services/Employees";
import ActiveEmployeeFilterModal, {
  FormProps,
} from "../Modals/active-employees-filter-modal";
import { Employees } from "../../interfaces/employee";
import { exportToCsv, exportToPDF } from "../../config";
import DeleteEmployeeModal from "../Modals/delete-employee-modal";
import Content from "./GetAPIS/get-active-employees-api";
import { useReactToPrint } from "react-to-print";
import SelectSearch from "react-select-search";

const ActiveEmployees = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.employee);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const tableRef = useRef(null);
  const [print, setPrint] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => tableRef.current,
    onAfterPrint: () => setPrint(false),
  });
  const handlePrinted = async () => {
    await setPrint(true);
    handlePrint();
  };
  const employees = useAppSelector((state) => state.employee.allEmployeesList);
  const activeAllEmployees = employees.filter(
    (item) => item.empStatus === "Employee",
  );
  // Options
  const options = activeAllEmployees.map((item) => ({
    name: item.empId + " - " + item.empName,
    value: item.empId,
  }));
  const options2 = activeAllEmployees.map((item) => ({
    name: `${item.empIqamaNo}` + " - " + item.empName,
    value: item.empId,
  }));
  // Pagination && Filter
  const [filterInput, setFilterInput] = useState<FormProps>({
    nationality: "",
    projectId: "",
    category: "",
    status: "",
    place: "",
    totalSalaryFrom: null,
    totalSalaryTo: null,
    passportExpiryFrom: "",
    passportExpiryTo: "",
  });
  const [filterSearch, setFilterSearch] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filteredData, setFilteredData] = useState<Employees[]>([]);
  const totalPages = Math.ceil(state.activeTotalRecords / state.activePageSize);
  const filterTotalPages = Math.ceil(
    state.filterActiveTotalRecords / state.filterActivePageSize,
  );
  const [deletedEmpId, setDeletedEmpId] = useState<number>(0);
  // show 5 pages at a time in pagination bar and rest will be shown in next and previous
  const showPages = 5;
  const startPage =
    Math.floor((state.activePageNo - 1) / showPages) * showPages;
  const endPage = startPage + showPages;
  const pages = [...Array(totalPages).keys()].slice(startPage, endPage);
  const filterStartPage =
    Math.floor((state.filterActivePageNo - 1) / showPages) * showPages;
  const filterEndPage = filterStartPage + showPages;
  const filterPages = [...Array(filterTotalPages).keys()].slice(
    filterStartPage,
    filterEndPage,
  );
  // handle go to page number that is after the 4 pages of current page
  const handleGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.activePageNo > totalPages - showPages) {
      return;
    }
    dispatch(setActivePageNo(state.activePageNo + showPages));
  };

  const handleGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.activePageNo <= 5) {
      return;
    }
    dispatch(setActivePageNo(state.activePageNo - showPages));
  };

  const handleFilterGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.filterActivePageNo > filterTotalPages - showPages) {
      return;
    }
    dispatch(setFilterActivePageNo(state.filterActivePageNo + showPages));
  };

  const handleFilterGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.filterActivePageNo <= 5) {
      return;
    }
    dispatch(setFilterActivePageNo(state.filterActivePageNo - showPages));
  };

  const totalCount = filterSearch
    ? state.filterActiveTotalRecords
    : state.activeTotalRecords;

  const handlePageChange = (page: any) => {
    dispatch(setActivePageNo(page));
  };

  const handleFilterPageChange = (page: any) => {
    dispatch(setFilterActivePageNo(page));
  };

  const handlePageSizeChange = (event: any) => {
    const selectedPageSize = parseInt(event.target.value, 10); // Parse the value to a number
    if (filterSearch) {
      dispatch(setFilterActivePageSize(selectedPageSize));
    } else {
      dispatch(setActivePageSize(selectedPageSize));
    }
  };
  useEffect(() => {
    setFilteredData(state.activeEmployees);
  }, [state.activeEmployees]);
  // Employees List
  const tableData = {
    tableHeading: [
      "Sr. No.",
      "Emp Code",
      "Name",
      "Category",
      "Place",
      "Phone",
      "Iqama No.",
      "Nationality",
      "Working Status",
      "Total Salary",
      "Work Permit Validty",
      "Status",
      "Actions",
    ],
    tableData: filteredData,
  };

  const csvData = tableData.tableData.map((item, index) => ({
    "Sr. No.": index + 1,
    "Emp Code": item.empId,
    Name: item.empName,
    Category: item.empCategory,
    Place: item.empPlace,
    Phone: item.empPhone,
    "Iqama No.": item.empIqamaNo,
    Nationality: item.empNationality,
    "Working Status": item.empWorking,
    "Total Salary": item.empTotalSalary,
    "Work Permit Validty": item.workPermitExpiry,
    Status: item.empStatus,
  }));

  const handleReset = () => {
    setQuery("");
    setSearch("");
    setFilteredData(state.employees);
    setFilterSearch(false);
    setFilterInput({
      nationality: "",
      projectId: "",
      category: "",
      status: "",
      place: "",
      totalSalaryFrom: null,
      totalSalaryTo: null,
      passportExpiryFrom: "",
      passportExpiryTo: "",
    });
  };

  const twoWords = (name: string) => {
    const words = name.split(" ");
    return words[0] + " " + words[1];
  };

  const showWorkPermitStatus = (workPermit: string) => {
    const expiryDate = new Date(workPermit);
    if (expiryDate.toLocaleDateString() === "Invalid Date") {
      return workPermit;
    }
    if (expiryDate < new Date()) {
      return "Expired";
    }
    return expiryDate.toLocaleDateString();
  };

  return (
    <div className="user-list mt-2 section px-4 py-4">
      <Content />
      <ActiveEmployeeFilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        setEmployees={setFilteredData}
        employees={state.employees}
        query={query}
        filterSearch={filterSearch}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
        setFilterSearch={setFilterSearch}
      />
      <DeleteEmployeeModal
        empId={deletedEmpId}
        show={showDeleteModal}
        setShow={setShowDeleteModal}
      />
      <Row>
        <Col sm="12">
          <div className="section-heading ">
            <h1 className="heading mb-0">Active Employees</h1>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <div
                className=" d-flex align-items-center gap-2"
                style={{ width: "50%" }}
              >
                <Form.Group className="w-100" controlId="formBasicEmail">
                  <Form.Group className="position-relative input-design d-flex align-items-center">
                    <SelectSearch
                      options={options}
                      onChange={(value: any) => {
                        setQuery(value);
                        setFilterSearch(true);
                      }}
                      value={query}
                      search={true}
                      placeholder="Search Employee by Id/Name "
                    />
                    <img src={Common.ArrowDown} alt="message" />
                  </Form.Group>
                </Form.Group>
                <Form.Group className="w-100" controlId="formBasicEmail">
                  <Form.Group className="position-relative input-design d-flex align-items-center">
                    <SelectSearch
                      options={options2}
                      onChange={(value: any) => {
                        setQuery(value);
                        setFilterSearch(true);
                      }}
                      value={query}
                      search={true}
                      placeholder="Search Employee by Iqama No/Name "
                    />
                    <img src={Common.ArrowDown} alt="message" />
                  </Form.Group>
                </Form.Group>
                {query !== "" ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : filterSearch ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-4">
                <button
                  className="dropdown-toggle-white dropdown-toggle-blue"
                  type="button"
                  onClick={() => navigate("/employees/add-employee")}
                >
                  <img src={Common.Plus} height="22px" width="22px" alt="img" />{" "}
                  New
                </button>
                <button
                  id="dropdown-basic"
                  onClick={() => {
                    setShowFilterModal(true);
                  }}
                  className="dropdown-toggle-white dropdown-toggle-blue"
                >
                  Filter{" "}
                </button>
                <Dropdown
                  show={state.showActiveExportDropdown}
                  className="position-relative"
                >
                  <button
                    id="dropdown-basic"
                    onClick={() => {
                      dispatch(
                        setShowActiveExportDropdown(
                          !state.showActiveExportDropdown,
                        ),
                      );
                    }}
                    className=" bg-transparent dropdown-toggle-white"
                  >
                    Export{" "}
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="ms-2"
                      alt="down-arrow"
                    />
                  </button>
                  <Dropdown.Menu
                    className="mt-1 custom-scrollbar"
                    show={state.showActiveExportDropdown}
                  >
                    <Dropdown.Item
                      onClick={() => {
                        exportToCsv(csvData, "employees-list");
                        dispatch(setShowActiveExportDropdown(false));
                      }}
                    >
                      CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        dispatch(setShowActiveExportDropdown(false));
                        handlePrinted();
                      }}
                    >
                      PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </Col>
        <Col sm="12">
          {state.empLoading ? (
            <LoaderBlue height={70} width={70} />
          ) : profileData.permissions.showEmployee ? (
            <>
              {tableData.tableData.length > 0 ? (
                <div className="p-3" ref={tableRef}>
                  {print && (
                    <>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <img src={Common.Vision} width="110px" alt="logo" />
                        </div>
                        <div className="letter-logo text-center gap-2">
                          <img src={Common.LogoSVG} width="80px" alt="logo" />
                          <div className="mt-1">
                            <h3
                              className="logo-text mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "10px",
                                fontWeight: "700",
                              }}
                            >
                              Senyar Arabian Trading Co. LTD
                            </h3>
                            <h3
                              className="logo-text logo-arabic mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              شركة سنيار العربية التجارية المحدودة
                            </h3>
                          </div>
                        </div>
                      </div>
                      <h1 className="sub-heading text-center mt-3">
                        All Employees List
                      </h1>
                    </>
                  )}
                  <Table
                    striped
                    bordered
                    responsive
                    className={
                      print
                        ? "mt-5 text-center print-table"
                        : "mt-5 text-center"
                    }
                  >
                    <thead>
                      <tr className="border-0">
                        {tableData.tableHeading.map((item, index) => (
                          <th
                            className={
                              item === "Actions" && print
                                ? "border-0 d-none"
                                : "border-0"
                            }
                            key={index}
                          >
                            {item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.tableData.map((item, index) => (
                        <tr
                          key={index}
                          style={{ borderColor: "rgba(32, 32, 32, 0.20)" }}
                        >
                          <td>
                            {totalCount > 0
                              ? filterSearch
                                ? (state.filterActivePageNo - 1) *
                                    state.filterActivePageSize +
                                  index +
                                  1
                                : (state.activePageNo - 1) *
                                    state.activePageSize +
                                  index +
                                  1
                              : index + 1}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/employees/employee-detail/${item.empId}`,
                                {
                                  state: { empId: item.empId },
                                },
                              )
                            }
                          >
                            {item.empId}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/employees/employee-detail/${item.empId}`,
                                {
                                  state: { empId: item.empId },
                                },
                              )
                            }
                          >
                            {" "}
                            {print ? twoWords(item.empName) : item.empName}
                          </td>
                          <td>{item.empCategory}</td>
                          <td>{item.empPlace}</td>
                          <td>{item.empPhone}</td>
                          <td>{item.empIqamaNo}</td>
                          <td>{item.empNationality}</td>
                          <td>{item.empWorking}</td>
                          <td>{item.empTotalSalary}</td>
                          <td>
                            <p
                              className={
                                showWorkPermitStatus(item.workPermitExpiry) ===
                                "Expired"
                                  ? "mb-0 py-1 px-4 text-white mx-auto"
                                  : "mb-0 py-1 px-4 mx-auto"
                              }
                              style={{
                                background:
                                  showWorkPermitStatus(
                                    item.workPermitExpiry,
                                  ) === "Expired"
                                    ? "rgba(235, 78, 43, 1)"
                                    : "",
                                width: "fit-content",
                                borderRadius: "20px",
                              }}
                            >
                              {showWorkPermitStatus(item.workPermitExpiry)}
                            </p>
                          </td>
                          <td>{item.empStatus}</td>
                          {!print && (
                            <td>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() =>
                                  navigate(
                                    `/employees/employee-detail/${item.empId}`,
                                    { state: { empId: item.empId } },
                                  )
                                }
                              >
                                <img
                                  src={Common.Eye}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                              <button
                                className="btn p-0 border-0 me-2"
                                onClick={() => {
                                  navigate(
                                    `/employees/edit-employee/${item.empId}`,
                                    { state: { empId: item.empId } },
                                  );
                                }}
                              >
                                <img
                                  src={SettingsIcons.EditUser}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>

                              <button
                                className="btn p-0 border-0"
                                onClick={() => {
                                  setDeletedEmpId(item.empId);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <img
                                  src={SettingsIcons.Delete}
                                  width="24px"
                                  height="24px"
                                  alt="edit-user"
                                />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center mt-5">
                  <h1 className="heading">No Employees Found</h1>
                </div>
              )}
            </>
          ) : (
            <div className="text-center mt-5">
              <h1 className="heading">Unauthorized Access</h1>
            </div>
          )}
        </Col>
        {filteredData.length > 0 && (
          <Col sm="12">
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <Form.Group
                className="d-flex align-items-center gap-2"
                controlId="formBasicEmail"
              >
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "14px", width: "230px" }}
                >
                  Entries per page
                </h4>
                <Form.Select
                  name="activePageSize"
                  onChange={handlePageSizeChange}
                  value={
                    filterSearch
                      ? state.filterActivePageSize.toString()
                      : state.activePageSize.toString()
                  }
                  className="custom-input border-fill custom-height"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="100">100</option>
                  <option value="10000">All</option>
                </Form.Select>
              </Form.Group>
              <div className="pagination-custom">
                <Pagination className="justify-content-end">
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterGoToPreviousPages()
                        : handleGoToPreviousPages()
                    }
                  >
                    <img src={Common.DoubleArrowLeft} alt="arrow" />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterActivePageNo > 1
                              ? state.filterActivePageNo - 1
                              : 1,
                          )
                        : handlePageChange(
                            state.activePageNo > 1 ? state.activePageNo - 1 : 1,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-left"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  {filterSearch ? (
                    <>
                      {/* render filter pages here */}
                      {filterPages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handleFilterPageChange(page + 1)}
                          className={
                            page + 1 === state.filterActivePageNo
                              ? "active-page"
                              : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* render pages here */}
                      {pages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handlePageChange(page + 1)}
                          className={
                            page + 1 === state.activePageNo ? "active-page" : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  )}
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterActivePageNo < filterTotalPages
                              ? state.filterActivePageNo + 1
                              : filterTotalPages,
                          )
                        : handlePageChange(
                            state.activePageNo < totalPages
                              ? state.activePageNo + 1
                              : totalPages,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-right"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() => {
                      filterSearch
                        ? handleFilterGoToNextPages()
                        : handleGoToNextPages();
                    }}
                  >
                    <img src={Common.DoubleArrowRight} alt="arrow" />
                  </Pagination.Item>
                </Pagination>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default ActiveEmployees;
